import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // TODO: Send error to logging service in production
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-3 px-8 text-center text-ink">
          <span className="w-14 h-14 rounded-full bg-bad-soft text-bad inline-flex items-center justify-center">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l10 18H2z" /><path d="M12 10v4M12 17h.01" /></svg>
          </span>
          <h1 className="text-xl font-black mt-2">頁面出了點問題</h1>
          <p className="text-sm text-muted leading-relaxed max-w-xs">重新整理通常就能解決。如果一直發生，請在 LINE 群組告訴我們。</p>

          {import.meta.env.DEV && this.state.error && (
            <div className="w-full max-w-xs rounded-[14px] bg-bad-soft p-3 text-left">
              <p className="text-xs font-mono text-bad break-all">{this.state.error.toString()}</p>
              {this.state.errorInfo && (
                <pre className="text-[11px] text-bad mt-2 overflow-auto max-h-32">{this.state.errorInfo.componentStack}</pre>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2 w-full max-w-xs mt-4">
            <button type="button" onClick={this.handleRetry} className="h-14 rounded-[16px] bg-primary text-white font-bold">重試</button>
            <button type="button" onClick={this.handleGoHome} className="h-12 rounded-[16px] bg-soft text-ink font-bold">回首頁</button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
